import React, { useState } from 'react';
import { Search, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, DollarSign, Scale, FileText, Users, ChevronDown, Lightbulb } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ServiceHeroForm from '../../components/ServiceHeroForm';
import RegistrationModal from '../../components/RegistrationModal';

const PatentSearchesPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Avoid Infringement',
      description: 'Prevent costly patent infringement lawsuits by identifying existing patents'
    },
    {
      icon: CheckCircle,
      title: 'Validate Innovation',
      description: 'Confirm your invention is truly novel and non-obvious'
    },
    {
      icon: TrendingUp,
      title: 'Competitive Intelligence',
      description: 'Understand the patent landscape and your competitors\' strategies'
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: 'Get comprehensive search results within 3-5 business days'
    }
  ];

  const whatWeSearch = [
    'USPTO patent database (all active patents)',
    'Published patent applications',
    'International patents (PCT, EPO, JPO)',
    'Prior art literature and publications',
    'Technical journals and conference papers',
    'Non-patent literature (NPL)',
    'Foreign patent offices',
    'Industry standards and specifications'
  ];

  const searchTypes = [
    {
      name: 'Patentability Search',
      price: '$599',
      description: 'Preliminary search to assess if your invention can be patented',
      features: ['USPTO database search', 'Published applications', 'International patents', 'Prior art analysis', 'Patentability opinion', '5-day turnaround']
    },
    {
      name: 'Freedom to Operate',
      price: '$1,299',
      description: 'Comprehensive search to ensure you can commercialize without infringement',
      features: ['All patentability features', 'Active patents only', 'Claims analysis', 'Infringement risk assessment', 'Detailed legal opinion', '7-day turnaround']
    },
    {
      name: 'State of the Art',
      price: '$1,999',
      description: 'Exhaustive search for competitive intelligence and R&D planning',
      features: ['All database searches', 'Technology landscape mapping', 'Competitor patent portfolios', 'Trend analysis', 'Strategic recommendations', '10-day turnaround']
    }
  ];

  const caseStudies = [
    {
      title: 'Medical Device Manufacturer Avoids $5M Lawsuit',
      scenario: 'A startup developed a revolutionary medical device ready for market launch.',
      problem: 'After launching, received cease & desist from competitor with blocking patent.',
      solution: 'Our FTO search would have identified 3 blocking patents, allowing design-around solutions.',
      outcome: 'Saved $5M in litigation costs and 2 years of product delays.'
    },
    {
      title: 'Software Company Pivots Before $500K Investment',
      scenario: 'Tech company about to file patent and invest in development.',
      problem: 'Our patentability search found nearly identical prior art from 2019.',
      solution: 'Advised on specific modifications to achieve patentability and freedom to operate.',
      outcome: 'Successfully obtained patent with modified claims, avoided rejection and reapplication costs.'
    }
  ];

  const timeline = [
    { time: 'Day 1', activity: 'Submit detailed invention disclosure and technical specifications' },
    { time: 'Day 2-3', activity: 'Patent attorney conducts multi-database search using specialized tools' },
    { time: 'Day 4-5', activity: 'Analysis of relevant patents, prior art, and legal landscape' },
    { time: 'Day 5-7', activity: 'Receive comprehensive report with patentability opinion and strategy' }
  ];

  const faqs = [
    {
      question: 'What is the difference between a patentability search and a freedom to operate search?',
      answer: 'A patentability search determines if your invention is novel and can be patented by looking at all prior art including expired patents. A freedom to operate (FTO) search identifies active patents that could prevent you from making, using, or selling your invention commercially. You need both for complete protection: patentability before filing, FTO before launching.'
    },
    {
      question: 'Can I just search Google Patents for free?',
      answer: 'While Google Patents is a useful starting point, it lacks the sophisticated search tools and legal expertise needed for thorough prior art searches. Our patent attorneys use specialized databases (Derwent, LexisNexis, PatBase), Boolean search strategies, classification codes, and citation analysis that go far beyond basic keyword searches. We also analyze non-patent literature that Google Patents does not cover.'
    },
    {
      question: 'How far back does a patent search go?',
      answer: 'Our searches cover all relevant prior art with no time limit. This includes all patents ever issued (even expired ones matter for patentability), all published applications, international filings under PCT and Paris Convention, scientific literature, technical publications, and even product documentation. Anything publicly disclosed before your filing date counts as prior art.'
    },
    {
      question: 'What happens if the search finds similar patents?',
      answer: 'Finding similar patents does not necessarily mean you cannot patent your invention. Our attorneys analyze the claims of similar patents to identify differences and assess patentability. We provide strategic recommendations on: claim drafting strategies, design-around solutions, continuation applications, licensing opportunities, or alternative protection methods like trade secrets.'
    },
    {
      question: 'Do I need a patent search before filing a provisional patent?',
      answer: 'While not legally required, it is highly recommended. A provisional patent gives you 12 months before filing a non-provisional application. Using this time to conduct a thorough search allows you to: refine your invention to overcome prior art, save $5,000-$15,000 in non-provisional filing costs if not patentable, strengthen your claims, and identify freedom to operate issues early.'
    },
    {
      question: 'What if my invention is in a highly technical field?',
      answer: 'We have patent attorneys and agents with technical backgrounds in biotechnology, pharmaceuticals, software, electrical engineering, mechanical engineering, chemistry, and more. For highly specialized fields, we partner with technical experts who understand your invention\'s nuances and can conduct more effective searches in specialized databases and literature.'
    }
  ];

  const commonMistakes = [
    { mistake: 'Only searching exact keywords', impact: 'Misses patents using different terminology for same concept' },
    { mistake: 'Ignoring international patents', impact: 'US patents cite foreign prior art; missing it leads to rejection' },
    { mistake: 'Not reviewing non-patent literature', impact: 'Academic papers and publications can block patentability' },
    { mistake: 'Searching too narrowly', impact: 'Patent examiners search broadly; you should too' },
    { mistake: 'Skipping FTO before launch', impact: 'Risk expensive infringement litigation after product launch' }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="max-w-3xl">
              <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Patent Search Services
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Comprehensive <span className="text-[#168356]">Patent Searches</span> & Prior Art Analysis
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Protect your innovation with thorough patent searches. Our expert attorneys search millions of patents and prior art to ensure your invention is patentable and free to operate.
              </p>
              <div className="flex gap-4">
                <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">
                  Start Your Search Now
                </button>
                <button onClick={() => openModal('registration')} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                  View Sample Report
                </button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#168356]" />
                  Patent Attorney Review
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#168356]" />
                  5-7 Day Delivery
                </span>
                <span className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#168356]" />
                  Multi-Database Search
                </span>
              </div>
            </div>
          </div>
          <ServiceHeroForm title="Start Your Patent Search" buttonText="Start Patent Search" />
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">$5M+</div>
              <div className="text-gray-600 text-sm">Average infringement lawsuit cost</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">11M+</div>
              <div className="text-gray-600 text-sm">Active US patents in database</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">70%</div>
              <div className="text-gray-600 text-sm">Patents have prior art conflicts</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">95%</div>
              <div className="text-gray-600 text-sm">Our search accuracy rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is Patent Search?
              </div>
              <h2 className="text-4xl font-bold text-black mb-6">Validate Your Innovation Before Filing</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A patent search is a critical due diligence step before filing a patent application. It involves systematically searching through millions of patents, published applications, and technical literature to determine if your invention is truly novel and non-obvious.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Our patent attorneys use specialized databases and advanced search techniques that go far beyond simple keyword searches. We analyze patent classifications, citations, semantic relationships, and technical specifications to identify relevant prior art that could affect your patent's validity or enforceability.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                There are three main types of patent searches: Patentability searches (is it novel?), Freedom to Operate searches (can you commercialize it?), and State of the Art searches (what's the competitive landscape?). Each serves a different strategic purpose in your innovation journey.
              </p>
              <p className="text-lg font-semibold text-black">
                Don't risk $10,000-$15,000 in patent filing costs and years of development on an invention that can't be patented or commercialized. Our search provides clarity and strategic direction.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-black mb-6">What We Search:</h3>
              <ul className="space-y-3">
                {whatWeSearch.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 italic">
                  "We search across 100+ million patent documents worldwide using specialized patent attorney tools"
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Why Patent Search is Essential</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Filing a patent without a thorough search can lead to devastating financial and legal consequences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 border-l-4 border-red-500 shadow-lg">
              <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-2xl font-bold text-black mb-4">Without a Search</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>$15K+ wasted fees:</strong> Patent attorney fees and USPTO costs are non-refundable</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>$5M+ lawsuit risk:</strong> Patent infringement lawsuits average $5-10M in legal costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>2-3 year delays:</strong> Patent prosecution takes years; rejection restarts the clock</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Invalid patents:</strong> Patents granted without search are vulnerable to invalidation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Market blocks:</strong> Cannot launch product if infringing active patents</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Investor loss:</strong> VCs and investors require FTO analysis before funding</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 border-l-4 border-[#168356] shadow-lg">
              <CheckCircle className="w-12 h-12 text-[#168356] mb-4" />
              <h3 className="text-2xl font-bold text-black mb-4">With Our Search</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Smart investment:</strong> $599-$1,999 search vs $15K+ wasted fees</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Risk mitigation:</strong> Identify infringement risks before market launch</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Stronger patents:</strong> Craft claims that overcome prior art from the start</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Strategic advantage:</strong> Understand competitive landscape and gaps</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Clear path forward:</strong> Know exactly what can and cannot be patented</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Investor confidence:</strong> Show thorough due diligence to funding sources</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Search Types & Pricing */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Choose Your Search Type</h2>
            <p className="text-lg text-gray-600">Select the search that matches your stage and needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {searchTypes.map((type, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${index === 1 ? 'border-[#168356] transform scale-105' : 'border-gray-200'}`}>
                {index === 1 && (
                  <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{type.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{type.price}</div>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <ul className="space-y-3 mb-6">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold transition-all ${index === 1 ? 'bg-[#d29b00] text-white hover:bg-[#a87b00]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Real-World Success Stories</h2>
            <p className="text-lg text-gray-600">See how patent searches protected these innovators</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 uppercase mb-2">Scenario</div>
                    <p className="text-gray-700">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-500 uppercase mb-2">Problem</div>
                    <p className="text-gray-700">{study.problem}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#168356] uppercase mb-2">Our Solution</div>
                    <p className="text-gray-700">{study.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-green-600 uppercase mb-2">Outcome</div>
                    <p className="text-gray-700 font-semibold">{study.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Common Patent Search Mistakes</h2>
            <p className="text-lg text-gray-600">Avoid these critical errors that lead to patent rejection</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonMistakes.map((item, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-xl p-6">
                <AlertTriangle className="w-8 h-8 text-red-500 mb-4" />
                <h3 className="text-lg font-bold text-black mb-2">{item.mistake}</h3>
                <p className="text-gray-700 text-sm">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Search Process & Timeline</h2>
            <p className="text-lg text-gray-600">Comprehensive results delivered in 5-7 business days</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#168356]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#168356] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Benefits of Our Patent Search</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default PatentSearchesPage;